/**
 * Pointers to commands still in flight, kept across a reload (IR-126 §7.2.2).
 *
 * Deliberately the smallest thing that survives a page load: `{projectId,
 * taskId, commandId, baseSeq}` per outstanding command, and nothing else.
 *
 * ## Why this does not contradict §7.2.2
 *
 * That clause says the locally-advanced `base_seq` "does not survive reload and
 * must be re-read". It is about a **guess outranking the server** — the ledger's
 * prediction is a forecast, and a forecast that outlives the page would beat a
 * snapshot that is now authoritative.
 *
 * A command id is not a forecast. It is a pointer to a document the server
 * holds and will resolve on its own; re-attaching to it asks the server what
 * happened rather than telling it. The ledger still starts empty after a reload
 * — it is a module-level `Map` and nothing here writes to it — and `base_seq`
 * is still read from the task. `baseSeq` is stored only so a recovered outcome
 * can be reported with the same shape as a live one.
 *
 * ## sessionStorage, not localStorage
 *
 * The ledger is per-tab, and this has to match its scope. `localStorage` is
 * shared across tabs, so one tab's pending commands would appear in another —
 * which would show a pending badge on a card that tab never touched, and let
 * two tabs both report the same outcome.
 *
 * ## The honest limit
 *
 * A crash, or a closed tab, loses the pointer. The command still applies
 * server-side; its outcome simply goes unnarrated. Querying the queue by status
 * would not have that hole — but it needs client read access on
 * `command_queues/**` and a composite index, neither of which exists, and both
 * on the "we write it, Aymeric publishes" path. Not worth parking the card.
 */
import { logger } from '@/utils/logger'

const STORAGE_KEY = 'mac.task_commands.pending'

export interface CommandPointer {
  projectId: string
  taskId: string
  commandId: string
  /**
   * What the command was composed against — for reporting, not for re-basing.
   *
   * **Absent for a `task_create`**, which has no `base_seq` at all: there is no
   * prior revision for it to be stale against (§4.3). Optional rather than a
   * sentinel, because a fabricated sequence number in a persisted pointer reads
   * as real to whoever touches it next — and reintroducing `base_seq` by the
   * back door is exactly what splitting create off from edit was meant to
   * avoid.
   */
  baseSeq?: number
}

function storage(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    // Private-mode Safari and some embedded webviews throw on access. Recovery
    // is a nicety; failing to read it must never stop the board loading.
    return null
  }
}

function readAll(): CommandPointer[] {
  const store = storage()
  if (!store) return []
  try {
    const raw = store.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    // Tolerant reader: a shape from an older build must not throw on load.
    return parsed.filter(
      (p): p is CommandPointer =>
        typeof p === 'object' &&
        p !== null &&
        typeof (p as CommandPointer).projectId === 'string' &&
        typeof (p as CommandPointer).taskId === 'string' &&
        typeof (p as CommandPointer).commandId === 'string' &&
        // Absent is legitimate (a create); present-but-not-a-number is corrupt.
        // Dropping the absent case would silently discard every create pointer
        // and re-open the reload hole for the one operation where losing it is
        // most visible — the user sees neither a proposal nor a board row.
        ((p as CommandPointer).baseSeq === undefined ||
          typeof (p as CommandPointer).baseSeq === 'number'),
    )
  } catch (e) {
    logger.warn('task command pointers: unreadable, starting empty', e)
    return []
  }
}

function writeAll(pointers: CommandPointer[]): void {
  const store = storage()
  if (!store) return
  try {
    if (pointers.length === 0) store.removeItem(STORAGE_KEY)
    else store.setItem(STORAGE_KEY, JSON.stringify(pointers))
  } catch (e) {
    // Quota, or a storage that refuses writes. Losing a pointer costs a
    // narrated outcome, not data — so it is a warning, not a failure.
    logger.warn('task command pointers: could not be saved', e)
  }
}

/** Record a command that is now in flight. */
export function rememberCommand(pointer: CommandPointer): void {
  const kept = readAll().filter((p) => p.commandId !== pointer.commandId)
  writeAll([...kept, pointer])
}

/** Drop a command that has settled, whatever it settled as. */
export function forgetCommand(commandId: string): void {
  const kept = readAll().filter((p) => p.commandId !== commandId)
  writeAll(kept)
}

/** Everything still outstanding, optionally narrowed to one project. */
export function pendingCommands(projectId?: string): CommandPointer[] {
  const all = readAll()
  return projectId ? all.filter((p) => p.projectId === projectId) : all
}

/** Test seam. Never call from application code. */
export function _clearPointers(): void {
  writeAll([])
}
